import { useCallback, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { SavedRow } from 'modules/dragAndDropEditor/types';
import { getPageBeeBuilderMetadata } from 'modules/dragAndDropEditor/utils';
import noop from 'utils/noop';
import { Nullish } from 'utils/tsHelpers';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { EditSavedRowDesignState } from '../context/EditSavedRowDesignContext';
import { useSaveTemplate } from './useSaveTemplate';

export const useEditSavedRowDesignState = (): EditSavedRowDesignState => {
  const [savedRow, setSavedRow] = useState<Nullish<SavedRow>>(); // The row that is being edited
  const [beeBuilderHTML, setBeeBuilderHTML] = useState<string>(); // NOTE: the html **may be outdated** until the user saves the row, which is when it is generated.
  const [pageBeeBuilderMetadata, setPageBeeBuilderMetadata] = useState<string>(); // NOTE: this is not just the row bee metadata, it is a whole template metadata with a single row in it.
  const [initialPageBeeBuilderMetadata, setInitialPageBeeBuilderMetadata] = useState<string>(); // we may not need this state, we could use the `savedRow` to get the initial metadata. leaving it for now.

  const {
    formState: { defaultValues },
    reset: resetLiveTemplate,
  } = useFormContext();
  const { saveTemplateAndRefetch } = useSaveTemplate();
  const { setDragAndDropInitValue } = useEditableFieldsContext();

  /**
   * Tracks whether the drag and drop editor is in single row edit mode.
   *
   * NOTE: We have some bee use-cases for this state which don't live within the react-lifecycle,
   * so we need to use refs to access the correct latest value.
   *
   * DANGER: DO NOT use `_setIsEditingSingleRow` directly, use `setIsEditingSingleRow` instead. This is to ensure that the ref and state are always in sync.
   */
  const [isEditingSavedRowDesign, _setIsEditingSingleRow] = useState<boolean>(false);
  const isEditingSavedRowDesignRef = useRef<boolean>(isEditingSavedRowDesign);

  const setIsEditingSingleRow = useCallback(
    (value: boolean) => {
      _setIsEditingSingleRow(value);
      isEditingSavedRowDesignRef.current = value;
    },
    [isEditingSavedRowDesignRef, _setIsEditingSingleRow],
  );

  /**
   * This ref is used to switch between Edit Single Row Mode and regular template editing,
   * it is set in the **DragAndDropEditor** component.
   */
  const toggleSavedRowDesignEditorRef =
    useRef<Nullish<(pageBeeBuilderMetadata?: string) => void>>(null);

  const htmlChanged = Boolean(
    beeBuilderHTML &&
      savedRow?.primaryContent?.html &&
      beeBuilderHTML !== savedRow?.primaryContent?.html,
  );

  const metadataChanged = Boolean(
    pageBeeBuilderMetadata &&
      initialPageBeeBuilderMetadata &&
      pageBeeBuilderMetadata !== initialPageBeeBuilderMetadata,
  );

  const hasUnsavedSavedRowDesignChanges = htmlChanged || metadataChanged;

  /**
   * Updates the state for editing a single row
   * @param row The updated version of the row being edited
   */
  const updateEditSavedRowDesign = (row: SavedRow) => {
    const template = getPageBeeBuilderMetadata(row);

    setSavedRow(row);
    setIsEditingSingleRow(true);
    setBeeBuilderHTML(row?.primaryContent?.html);
    setInitialPageBeeBuilderMetadata(template);
    setPageBeeBuilderMetadata(template);
  };

  /**
   * Sets initial state for starting to edit a single row
   * @param row The row being edited
   */
  const startEditSavedRowDesign = (row: SavedRow) => {
    updateEditSavedRowDesign(row);

    /**
     * Starts the editor in single row editing mode
     * @see https://github.com/Iterable/iterable-frontend/blob/5edd33f23c31b594e681510c51afa1ce9ba85752/src/modules/dragAndDropEditor/DragAndDropEditor.tsx#L595-L641
     */
    toggleSavedRowDesignEditorRef.current?.(getPageBeeBuilderMetadata(row));
  };

  /**
   * Resets all the state edit single row states
   */
  const resetEditSavedRowDesign = () => {
    setSavedRow(null);
    setIsEditingSingleRow(false);
    setBeeBuilderHTML(undefined);
    setPageBeeBuilderMetadata(undefined);
    setInitialPageBeeBuilderMetadata(undefined);
  };

  /**
   * Exits the single row edit mode and restart the editor in template editing mode
   */
  const onExitSavedRowDesignEditor = () => {
    resetEditSavedRowDesign();

    /**
     * Restart the editor in template editing mode
     * @see https://github.com/Iterable/iterable-frontend/blob/5edd33f23c31b594e681510c51afa1ce9ba85752/src/modules/dragAndDropEditor/DragAndDropEditor.tsx#L595-L641
     */
    toggleSavedRowDesignEditorRef.current?.();
  };

  /**
   * Resets the drag and drop template changes to the default template
   */
  const onResetDragAndDropTemplateChanges = useCallback(() => {
    resetLiveTemplate();
    setDragAndDropInitValue(defaultValues?.mediumData?.beeBuilderMetadata as string);
  }, [defaultValues, resetLiveTemplate, setDragAndDropInitValue]);

  /**
   * Saves the template for the saved row design flow
   */
  const saveTemplateForSavedRow = useCallback(
    (onSuccess: (data?: any) => void = noop, onError: (error?: unknown) => void = noop) => {
      const _onSuccess = (response?: any) => {
        const primaryContent = response?.data?.templateAndSingleContent?.primaryContent;
        const html = primaryContent?.html;
        const beeBuilderMetadata = primaryContent?.beeBuilderMetadata;

        const defaultTemplateValues = {
          ...defaultValues,
          mediumData: { ...defaultValues?.mediumData, beeBuilderMetadata, html },
        };

        resetLiveTemplate(defaultTemplateValues);
        setDragAndDropInitValue(beeBuilderMetadata);

        onSuccess(response);
      };

      saveTemplateAndRefetch(_onSuccess, onError);
    },
    [defaultValues, resetLiveTemplate, saveTemplateAndRefetch, setDragAndDropInitValue],
  );

  return {
    savedRow,
    hasUnsavedSavedRowDesignChanges,
    isEditingSavedRowDesign,
    isEditingSavedRowDesignRef,
    beeBuilderHTML,
    pageBeeBuilderMetadata,
    toggleSavedRowDesignEditorRef,
    setSavedRow,
    setBeeBuilderHTML,
    setPageBeeBuilderMetadata,
    startEditSavedRowDesign,
    updateEditSavedRowDesign,
    resetEditSavedRowDesign,
    onExitSavedRowDesignEditor,
    onResetDragAndDropTemplateChanges,
    saveTemplateForSavedRow,
  };
};
